import type { PropsWithChildren } from "react";
import { css, styled } from "styled-components";

import { LeftArrow, LockedContent, PinSolidIcon, SectionSubTitle, UIButton } from "common/components";
import { breakpoints } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { LoadMoreButton } from "domains/topicDetail/components/LoadMoreButton";
import { createTopicSelector } from "entities/topics/selectors";

const StyledSection = styled.div<{ $isHidden?: boolean; $isExpanded?: boolean }>`
  display: ${({ $isHidden }) => ($isHidden ? "none" : "block")};
  padding-top: ${({ $isExpanded }) => ($isExpanded ? 2 : 0)}rem;
  padding-bottom: 1.5rem;

  @media ${breakpoints.large} {
    padding-top: 0;
  }
`;

const StyledBadge = styled.span`
  transition: background-color 0.3s;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.blue};
  color: white;
  margin-right: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.7rem;
  line-height: 20px;
  letter-spacing: 1px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 10px;
  margin: 0 1rem;

  @media ${breakpoints.medium} {
    margin: 0 2rem;
  }

  @media ${breakpoints.large} {
    margin: 0;
    margin-right: 2rem;
  }

  @media ${breakpoints.extraLarge} {
    margin-right: 3rem;
  }
`;

const StyledInnerContainer = styled.div<{ $isLocked?: boolean }>`
  margin: 0 1rem;

  @media ${breakpoints.medium} {
    margin: 0 2rem;
  }

  @media ${breakpoints.large} {
    margin: 0;
    margin-right: 2rem;
  }

  @media ${breakpoints.extraLarge} {
    margin-right: 3rem;
  }

  ${({ $isLocked }) =>
    $isLocked &&
    css`
      filter: blur(10px);
    `}
`;

const StyledRelativeContent = styled.div`
  position: relative;
`;

const StyledBackButton = styled(UIButton)`
  margin-bottom: 1rem;
  margin-left: 1rem;
  @media ${breakpoints.medium} {
    margin-left: 2rem;
  }
  @media ${breakpoints.large} {
    margin-left: 0;
  }
`;

const StyledBackButtonIcon = styled(LeftArrow)`
  height: 15px;
  width: 15px;
  margin-right: 1rem;
`;

const StyledPinIcon = styled(PinSolidIcon)`
  color: ${({ theme }) => theme.colors.textFade};
`;

type TopicTitleIconVariant = "pin";

type TopicDetailSectionProps = PropsWithChildren & {
  display: boolean;
  filterCount?: number;
  hasNextPage?: boolean;
  isExpanded?: boolean;
  isHidden?: boolean;
  isHorizontal?: boolean;
  isLocked?: boolean;
  isPending?: boolean;
  seeAllLink?: string;
  selectSectionHandle?: () => void;
  title: string;
  topicId: string;
  iconVariant?: TopicTitleIconVariant;
};

export const TopicDetailSection = ({
  children,
  display,
  filterCount,
  hasNextPage = false,
  isExpanded,
  isHidden,
  isHorizontal,
  isLocked,
  isPending,
  seeAllLink,
  selectSectionHandle,
  title,
  topicId,
  iconVariant,
}: TopicDetailSectionProps) => {
  const translate = useTranslate();
  const topic = useSelector(createTopicSelector(topicId));
  if (!display && !isPending) {
    return null;
  }

  const onClick = selectSectionHandle ? () => selectSectionHandle() : undefined;

  const Icon = () => {
    switch (iconVariant) {
      case "pin":
        return <StyledPinIcon />;
      default:
        return null;
    }
  };

  return (
    <StyledSection $isExpanded={isExpanded} $isHidden={isHidden}>
      {isExpanded && (
        <StyledBackButton onClick={onClick} variant="neutral">
          <StyledBackButtonIcon />
          {topic?.name ?? translate("app.navigation.back")}
        </StyledBackButton>
      )}
      <StyledTitleContainer>
        <Icon />
        <SectionSubTitle seeAllLink={seeAllLink} title={title} />
      </StyledTitleContainer>
      <StyledRelativeContent>
        <StyledInnerContainer $isLocked={isLocked} inert={isLocked}>
          {children}
          {!isExpanded && (hasNextPage || (filterCount ?? 0) !== 0) && !isHorizontal && (
            <LoadMoreButton onClick={onClick}>
              {!!filterCount && <StyledBadge>{filterCount}</StyledBadge>}
            </LoadMoreButton>
          )}
        </StyledInnerContainer>
        {isLocked && <LockedContent trackPage="Topic detail" />}
      </StyledRelativeContent>
    </StyledSection>
  );
};
